// Bendri Fluent 2 pirminiai (primitive) komponentai — TIK tiems konceptams, kurių
// Fluent NETURI 1:1 atitikmens (badge tonų sistema, heading skalė, card/card-hover,
// section/container plotis, nav-item, statistikos kortelė, tuščios būsenos placeholder,
// puslapiavimas). Kur Fluent JAU turi tiesioginį komponentą (Button/Input/Checkbox/
// Dropdown/Spinner/Avatar/Tooltip/Dialog/Table) — naudoti TĄ Fluent komponentą TIESIOGIAI
// prie naudojimo vietos, NE per šį failą.
//
// Pakeičia src/admin/components/ui.jsx (kurio TIKSLI prop API čia išlaikyta ten, kur
// pagrįsta — mažesnis diffas ~150 naudojimo vietų) IR yra dabar prieinamas VISIEMS
// (vieši puslapiai anksčiau savo kito neturėjo, dabar turi tą patį).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  makeStyles, mergeClasses, tokens,
  Button as FluentButton, Spinner, Text, Input, FluentProvider, Checkbox,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  Toaster, useId, useToastController, Toast, ToastTitle,
  Menu, MenuTrigger, MenuPopover, MenuList, MenuItem,
  OverlayDrawer, DrawerBody,
  Breadcrumb as FluentBreadcrumb, BreadcrumbItem, BreadcrumbButton, BreadcrumbDivider,
} from '@fluentui/react-components';
import {
  DismissRegular, SearchRegular, ImageRegular, ChevronLeftRegular, ChevronRightRegular, MoreHorizontalRegular,
  ArrowSortRegular, ArrowSortUpRegular, ArrowSortDownRegular,
} from '@fluentui/react-icons';
import { goldPalette, fititLightTheme } from './brand';
import { API_BASE, API_HOST } from '../config.js';

// ── makeStyles hooks ─────────────────────────────────────────────────────────
const useCardStyles = makeStyles({
  base: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    transitionProperty: 'box-shadow, border-color, transform',
    transitionDuration: tokens.durationNormal,
  },
  hover: {
    ':hover': {
      boxShadow: tokens.shadow8,
      borderColor: tokens.colorNeutralStroke1,
      transform: 'translateY(-2px)',
    },
  },
  flat: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusXLarge,
    border: 'none',
  },
});

// Fluent 2's documented web type ramp (fluent2.microsoft.design/typography):
// Display 68/92, Large Title 40/52, Title 1 32/40, Title 2 28/36, Title 3 24/32,
// Subtitle 1 20/26, Subtitle 2 16/22, Body 1 14/20, Caption 1 12/16 — mapped onto
// the matching shipped tokens (fontSizeHero*/fontSizeBase*, lineHeightHero*/Base*)
// rather than ad-hoc rem values, so this primitive tracks the real Fluent scale.
const useHeadingStyles = makeStyles({
  display: { fontSize: tokens.fontSizeHero900, lineHeight: tokens.lineHeightHero900, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, letterSpacing: '-0.02em' },
  h1: { fontSize: tokens.fontSizeHero800, lineHeight: tokens.lineHeightHero800, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, letterSpacing: '-0.01em' },
  h2: { fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  h3: { fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  section: { fontSize: tokens.fontSizeHero800, lineHeight: tokens.lineHeightHero800, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, letterSpacing: '-0.01em' },
  sectionSub: { fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, color: tokens.colorNeutralForeground2 },
  eyebrow: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase', letterSpacing: '0.08em', color: tokens.colorBrandForeground2 },
  bodyLarge: { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, color: tokens.colorNeutralForeground2 },
});

const useBadgeStyles = makeStyles({
  base: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalS, paddingRight: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXXS, paddingBottom: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, borderRadius: tokens.borderRadiusCircular,
  },
  green: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2 },
  gray: { backgroundColor: tokens.colorNeutralBackground4, color: tokens.colorNeutralForeground2 },
  red: { backgroundColor: tokens.colorPaletteRedBackground2, color: tokens.colorPaletteRedForeground2 },
  yellow: { backgroundColor: tokens.colorPaletteYellowBackground2, color: tokens.colorPaletteDarkOrangeForeground2 },
  blue: { backgroundColor: tokens.colorPaletteBlueBackground2, color: tokens.colorPaletteBlueForeground2 },
  gold: { backgroundColor: goldPalette[100], color: goldPalette[700] },
});

const useLayoutStyles = makeStyles({
  section: { paddingTop: '5rem', paddingBottom: '5rem', paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL },
  sectionSm: { paddingTop: '3rem', paddingBottom: '3rem', paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL },
  containerXl: { maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto' },
  containerLg: { maxWidth: '64rem', marginLeft: 'auto', marginRight: 'auto' },
  containerMd: { maxWidth: '48rem', marginLeft: 'auto', marginRight: 'auto' },
  pageHero: { backgroundColor: tokens.colorNeutralForeground1, color: tokens.colorNeutralBackground1, paddingTop: '5rem', paddingBottom: '5rem', paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL, textAlign: 'center' },
});

const useNavItemStyles = makeStyles({
  base: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalM, paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalSNudge, paddingBottom: tokens.spacingVerticalSNudge,
    borderRadius: tokens.borderRadiusXLarge, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2, cursor: 'pointer', textDecorationLine: 'none',
    transitionProperty: 'background-color, color', transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground1 },
  },
  active: {
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover, color: tokens.colorBrandForeground2 },
  },
});

const usePageHeaderStyles = makeStyles({
  root: { display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalXXL },
  left: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, minWidth: 0 },
  actions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'nowrap', flexShrink: 0 },
});

const useRowActionsMenuStyles = makeStyles({
  wrap: { display: 'flex', justifyContent: 'flex-end' },
  trigger: { minWidth: 'unset' },
  danger: {
    color: tokens.colorPaletteRedForeground1,
    ':hover': { backgroundColor: tokens.colorPaletteRedBackground1, color: tokens.colorPaletteRedForeground1 },
  },
});

const useEmptyStateStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', paddingTop: '3.5rem', paddingBottom: '3.5rem', paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL },
  iconChip: { width: '56px', height: '56px', borderRadius: tokens.borderRadiusXLarge, backgroundColor: tokens.colorNeutralBackground3, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3, fontSize: '26px' },
});

const useOverlayStyles = makeStyles({
  backdrop: { position: 'fixed', inset: 0, zIndex: 50, backgroundColor: 'rgba(0,0,0,0.4)' },
  modalRoot: { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: tokens.spacingHorizontalM },
  modalPanel: { position: 'relative', backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadow28, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: tokens.spacingHorizontalXL, paddingRight: tokens.spacingHorizontalXL, paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0 },
  modalBody: { paddingLeft: tokens.spacingHorizontalXL, paddingRight: tokens.spacingHorizontalXL, paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL, overflowY: 'auto', flex: 1 },
  modalFooter: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS, paddingLeft: tokens.spacingHorizontalXL, paddingRight: tokens.spacingHorizontalXL, paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0 },
});

const useImageFieldStyles = makeStyles({
  drop: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM, borderRadius: tokens.borderRadiusXLarge,
    border: `2px dashed ${tokens.colorNeutralStroke2}`, cursor: 'pointer',
    transitionProperty: 'border-color, background-color', transitionDuration: tokens.durationFast,
    ':hover': { borderColor: tokens.colorNeutralStroke1 },
  },
  dragOver: { borderColor: tokens.colorBrandStroke1, backgroundColor: tokens.colorBrandBackground2 },
  thumb: { width: '56px', height: '56px', borderRadius: tokens.borderRadiusLarge, objectFit: 'cover', flexShrink: 0 },
  thumbPlaceholder: { width: '56px', height: '56px', borderRadius: tokens.borderRadiusLarge, backgroundColor: tokens.colorNeutralBackground3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: tokens.colorNeutralForeground3 },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1, marginTop: tokens.spacingVerticalXXS },
});

const usePaginationStyles = makeStyles({
  root: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: tokens.spacingHorizontalXXS },
  pageBtn: {
    minWidth: '32px', height: '32px', padding: 0, borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium,
  },
  gap: { paddingLeft: tokens.spacingHorizontalXXS, paddingRight: tokens.spacingHorizontalXXS, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
});

const useDataTableStyles = makeStyles({
  wrap: { overflowX: 'auto', backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusXLarge },
  headerCell: {
    textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1, backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: `inset 0 -1px 0 ${tokens.colorNeutralStroke2}`,
  },
  headerCellSortable: { cursor: 'pointer', userSelect: 'none', ':hover': { color: tokens.colorNeutralForeground1 } },
  headerCellInner: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS },
  sortIcon: { fontSize: '14px', display: 'inline-flex' },
  checkboxCell: {
    width: '32px', position: 'sticky', top: 0, zIndex: 1, backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: `inset 0 -1px 0 ${tokens.colorNeutralStroke2}`,
  },
  row: { cursor: 'pointer' },
  // maxWidth + overflow:hidden + textOverflow:ellipsis kartu su whiteSpace:nowrap
  // apkarpo tekstą, kuris netelpa į stulpelį (vietoj to, kad "uzliptų" ant
  // kaimyninio stulpelio) — pilnas tekstas vis tiek pasiekiamas per native
  // title tooltip (žr. DataTable renderCell fallback žemiau).
  cell: { whiteSpace: 'nowrap', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis' },
  editBtn: {
    textAlign: 'left', width: '100%', padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    marginLeft: `calc(${tokens.spacingHorizontalXS} * -1)`, borderRadius: tokens.borderRadiusMedium,
    border: 'none', background: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorBrandBackground2 },
  },
});

// ── Komponentai ──────────────────────────────────────────────────────────────

export function Card({ className, hover, flat, padding = tokens.spacingHorizontalL, children, ...rest }) {
  const s = useCardStyles();
  return (
    <div
      className={mergeClasses(s.base, hover && s.hover, flat && s.flat, className)}
      style={{ padding }}
      {...rest}
    >
      {children}
    </div>
  );
}

const HEADING_VARIANTS = { display: 'display', h1: 'h1', h2: 'h2', h3: 'h3', section: 'section', sectionSub: 'sectionSub', eyebrow: 'eyebrow', bodyLarge: 'bodyLarge' };
const HEADING_TAG = { display: 'h1', h1: 'h1', h2: 'h2', h3: 'h3', section: 'h2', sectionSub: 'p', eyebrow: 'span', bodyLarge: 'p' };
export function Heading({ variant = 'h1', as, className, children, ...rest }) {
  const s = useHeadingStyles();
  const Tag = as || HEADING_TAG[variant] || 'p';
  return <Tag className={mergeClasses(s[HEADING_VARIANTS[variant] || 'h1'], className)} {...rest}>{children}</Tag>;
}

export function Badge({ label, color = 'gray', className, children }) {
  const s = useBadgeStyles();
  return <span className={mergeClasses(s.base, s[color] || s.gray, className)}>{label ?? children}</span>;
}

export function NavItem({ active, className, children, ...rest }) {
  const s = useNavItemStyles();
  return <a className={mergeClasses(s.base, active && s.active, className)} {...rest}>{children}</a>;
}

// Standartinis admin puslapio antraštės blokas — BE jokios ikonos šalia
// pavadinimo (žr. 20 punktų sąrašo #16: "prie puslapio Title neturi būti
// jokio icon"). `icon` prop sąmoningai NEBEPRIIMAMAS/nenaudojamas — MS365
// admin center stiliaus antraštėse pavadinimas visada be piktogramos.
export function PageHeader({ title, subtitle, actions, children }) {
  const s = usePageHeaderStyles();
  const hs = useHeadingStyles();
  return (
    <div className={s.root}>
      <div className={s.left}>
        <div>
          <Heading as="h1" variant="h1" className={hs.h1}>{title}</Heading>
          {subtitle && <Text size={300} style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS, display: 'block' }}>{subtitle}</Text>}
          {children}
        </div>
      </div>
      {actions && <div className={s.actions}>{actions}</div>}
    </div>
  );
}

// Standartinis lentelės eilutės veiksmų mygtukas — trys taškai, paspaudus
// atsidaro Fluent <Menu> su konfigūruojamais veiksmais (žr. 20 punktų sąrašo
// #16: "lentelese kiekvienoj eilutei turi būti 3 dots ir ant ju paspaudus
// atsidaro action menu" — pakeičia anksčiau atskirus Redaguoti/Ištrinti
// mygtukus kiekvienoje eilutėje).
// `actions`: [{ label, icon, onClick, danger }, ...]
export function RowActionsMenu({ actions }) {
  const s = useRowActionsMenuStyles();
  return (
    <div className={s.wrap}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <FluentButton
            appearance="subtle"
            shape="circular"
            size="small"
            icon={<MoreHorizontalRegular />}
            aria-label="Daugiau veiksmų"
            className={s.trigger}
            onClick={(e) => e.stopPropagation()}
          />
        </MenuTrigger>
        <MenuPopover onClick={(e) => e.stopPropagation()}>
          <MenuList>
            {actions.map((a, i) => (
              <MenuItem
                key={a.label ?? i}
                icon={a.icon}
                className={a.danger ? s.danger : undefined}
                onClick={a.onClick}
              >
                {a.label}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
}

// items: [{ label, to }, ...] — paskutinis be `to` visada rodomas kaip einamasis
// (plain tekstas, ne nuoroda), nesvarbu, ar `to` jam perduotas.
//
// Naudoja TIKRĄ Fluent <Breadcrumb>/<BreadcrumbButton> (ne custom <nav>/<span>
// implementaciją) — žr. 40 punktų sąrašo #3 (site). `BreadcrumbButton` viduje
// naudoja `useButtonBase_unstable`, kuris (skirtingai nei NavItem/NavSubItem)
// DESTRUKTŪRIZUOJA `as` propą ir jį NEPERDUODA toliau — jis tik nusprendžia
// 'a' vs 'button' pagal `as`/`href` TRUTHINESS, tad `as={Link}` čia neveiktų
// TAIP PAT KAIP NavItem atveju (žr. AdministrationMenu.jsx komentarą) — vietoj
// to naudojamas TAS PATS sprendimas: `href` + savas onClick su preventDefault
// + client-side navigate().
// Fluent `BreadcrumbButton` turi savo vidinį 6px kairįjį paddingą (hover/focus
// hit-target) — be kompensacijos pirmojo elemento TEKSTAS prasidėdavo 6px į
// dešinę nuo konteinerio krašto, tad nesutapdavo su po juo einančio <h1>
// antraštės tekstu, kuris jokio tokio paddingo neturi (žr. /admin/bugs
// "Title turi lygiuoti su breadcrumb tekstu").
const useBreadcrumbStyles = makeStyles({ root: { marginLeft: '-6px' } });

export function Breadcrumb({ items, className }) {
  const navigate = useNavigate();
  const s = useBreadcrumbStyles();
  return (
    <FluentBreadcrumb aria-label="Trupinių takelis" className={mergeClasses(s.root, className)}>
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <React.Fragment key={item.label}>
            <BreadcrumbItem>
              {isLast ? (
                <BreadcrumbButton current>{item.label}</BreadcrumbButton>
              ) : (
                <BreadcrumbButton href={item.to} onClick={(e) => { e.preventDefault(); navigate(item.to); }}>
                  {item.label}
                </BreadcrumbButton>
              )}
            </BreadcrumbItem>
            {!isLast && <BreadcrumbDivider />}
          </React.Fragment>
        );
      })}
    </FluentBreadcrumb>
  );
}

const STAT_CARD_COLORS = {
  primary: { bg: tokens.colorBrandBackground2, fg: tokens.colorBrandForeground2 },
  blue: { bg: tokens.colorPaletteBlueBackground2, fg: tokens.colorPaletteBlueForeground2 },
  yellow: { bg: tokens.colorPaletteYellowBackground2, fg: tokens.colorPaletteDarkOrangeForeground2 },
  green: { bg: tokens.colorPaletteGreenBackground2, fg: tokens.colorPaletteGreenForeground2 },
  red: { bg: tokens.colorPaletteRedBackground2, fg: tokens.colorPaletteRedForeground2 },
  graphite: { bg: tokens.colorNeutralBackground4, fg: tokens.colorNeutralForeground2 },
};

export function StatCard({ label, value, icon, color = 'primary', trend }) {
  const cs = useCardStyles();
  const c = STAT_CARD_COLORS[color] || STAT_CARD_COLORS.primary;
  return (
    <div className={cs.base} style={{ padding: tokens.spacingHorizontalL }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacingVerticalM }}>
        <Text size={200} weight="semibold" style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
        {icon && (
          <div style={{ width: 32, height: 32, borderRadius: tokens.borderRadiusMedium, backgroundColor: c.bg, color: c.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>
            {icon}
          </div>
        )}
      </div>
      <Text size={600} weight="bold" style={{ color: tokens.colorNeutralForeground1, display: 'block' }}>{value}</Text>
      {trend && <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS, display: 'block' }}>{trend}</Text>}
    </div>
  );
}

export function EmptyState({ icon, title, description, action }) {
  const s = useEmptyStateStyles();
  return (
    <div className={s.root}>
      <div className={s.iconChip}>{icon || <SearchRegular />}</div>
      <Text weight="semibold" size={300} style={{ color: tokens.colorNeutralForeground1 }}>{title}</Text>
      {description && <Text size={300} style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS, maxWidth: '24rem' }}>{description}</Text>}
      {action && <div style={{ marginTop: tokens.spacingVerticalM }}>{action}</div>}
    </div>
  );
}

// Drawer/Modal: portalinti į document.body — TAS PATS paternas ir priežastis, kaip
// senajame ui.jsx (viešo layout Header'is turi savo stacking kontekstą, portalas
// apeina jį). Rekomenduojama NAUJAM kodui naudoti Fluent <Drawer>/<Dialog> tiesiogiai;
// šie čia palaikomi kaip perėjimo pagalba esamiems ui.jsx skambinimams.
//
// BUG (rasta 2026-09-04, žr. 20 punktų sąrašo #8/#12): <FluentProvider> savo CSS
// kintamuosius (--colorNeutralBackground1 ir t.t.) uždeda TIK ant SAVO React
// medžio (DOM) pomedžio, NE globaliai ant <html>/<body>. createPortal(...,
// document.body) atvaizduoja turinį KAIP document.body BROLĮ/SESERĮ (ne
// palikuonį) tai FluentProvider medžio šakai — visi `tokens.*` (paprasčiausiai
// `var(--xyz)` eilutės) ten tampa NEAPIBRĖŽTI, o fonas/spalvos tyliai
// virsta permatomais, be jokios klaidos konsolėje. Sprendimas: PORTALINTĄ
// turinį reikia apvynioti SAVO <FluentProvider> egzemplioriumi (ta pati tema),
// kuris iš naujo įsteigia CSS kintamųjų sritį TAI konkrečiai poobrandies šakai.
// Standartinis Fluent OverlayDrawer vietoj rankinio createPortal(...,
// document.body) + hardcoded z-index — Fluent pats valdo savo portalo
// sluoksniavimą TOJE PAČIOJE sistemoje kaip likusi programa (jokio konflikto
// su sidebarSticky z-index'u), be pirmiau čia buvusio CSS-kintamųjų-portale
// bei sidenav-po-backdrop klaidų klasės (žr. /admin/bugs "Sutvarkyk z-index
// nes sidenav yra pasislepes po backdrop" — naudok standartinį Fluent Drawer).
export function SimpleDrawer({ open, onClose, width = '360px', children }) {
  return (
    <OverlayDrawer open={open} onOpenChange={(_e, data) => { if (!data.open) onClose(); }} position="end" style={{ width, maxWidth: '100%' }}>
      <DrawerBody>{children}</DrawerBody>
    </OverlayDrawer>
  );
}

export function SimpleModal({ open, onClose, title, children, footer, maxWidth = '42rem' }) {
  const s = useOverlayStyles();
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal((
    <FluentProvider theme={fititLightTheme} style={{ background: 'transparent' }}>
      <div className={s.modalRoot}>
        <div className={s.backdrop} onClick={onClose} />
        <div className={s.modalPanel} style={{ maxWidth, width: '100%' }}>
          <div className={s.modalHeader}>
            <Text size={500} weight="semibold">{title}</Text>
            <FluentButton appearance="subtle" icon={<DismissRegular />} aria-label="Uždaryti" onClick={onClose} />
          </div>
          <div className={s.modalBody}>{children}</div>
          {footer && <div className={s.modalFooter}>{footer}</div>}
        </div>
      </div>
    </FluentProvider>
  ), document.body);
}

export function PageSection({ small, hero, className, children, ...rest }) {
  const s = useLayoutStyles();
  return <section className={mergeClasses(hero ? s.pageHero : (small ? s.sectionSm : s.section), className)} {...rest}>{children}</section>;
}

export function Container({ size = 'xl', className, children, ...rest }) {
  const s = useLayoutStyles();
  const cls = size === 'lg' ? s.containerLg : size === 'md' ? s.containerMd : s.containerXl;
  return <div className={mergeClasses(cls, className)} {...rest}>{children}</div>;
}

export function LoadingSpinner({ label }) {
  return <Spinner size="medium" label={label} />;
}

// Drag-and-drop / click-to-upload image field — replaces manually typing a
// server file path. `value` is a path like "/uploads/materials/xyz.jpg";
// `onChange(path)` fires with the new path once the upload completes.
export function ImageField({ label, value, onChange, uploadUrl = `${API_BASE}/uploads/material-image` }) {
  const s = useImageFieldStyles();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(uploadUrl, { method: 'POST', credentials: 'include', body: formData });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Nepavyko įkelti');
      onChange(body.path);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: tokens.spacingVerticalXS }}>{label}</Text>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={mergeClasses(s.drop, dragOver && s.dragOver)}
      >
        {value ? (
          <img src={`${API_HOST}${value}`} alt="" className={s.thumb} />
        ) : (
          <div className={s.thumbPlaceholder}><ImageRegular /></div>
        )}
        <div>
          <Text size={200} className={s.hint}>
            {uploading ? 'Įkeliama...' : value ? 'Spustelėkite arba nutempkite, kad pakeistumėte' : 'Spustelėkite arba nutempkite paveikslėlį'}
          </Text>
          {uploadError && <Text size={200} block className={s.error}>{uploadError}</Text>}
        </div>
        <input
          ref={inputRef} type="file" accept="image/*" hidden
          onChange={(e) => doUpload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

// Page-number pagination (1 2 3 … N) with prev/next. Fluent has no native
// pager component — built from Button, shows up to 5 numbered pages around
// the current one plus first/last with an ellipsis when the range is large.
export function Pagination({ page, totalPages, onChange }) {
  const s = usePaginationStyles();
  if (totalPages <= 1) return null;
  const nums = new Set([1, totalPages, page, page - 1, page + 1]);
  const pages = [...nums].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);

  const items = [];
  pages.forEach((n, i) => {
    if (i > 0 && n - pages[i - 1] > 1) items.push(<span key={`gap-${n}`} className={s.gap}>…</span>);
    items.push(
      <FluentButton
        key={n} className={s.pageBtn} size="small"
        appearance={n === page ? 'primary' : 'subtle'}
        onClick={() => onChange(n)}
      >
        {n}
      </FluentButton>,
    );
  });

  return (
    <div className={s.root}>
      <FluentButton
        className={s.pageBtn} size="small" appearance="subtle" icon={<ChevronLeftRegular />}
        disabled={page <= 1} onClick={() => onChange(page - 1)}
      />
      {items}
      <FluentButton
        className={s.pageBtn} size="small" appearance="subtle" icon={<ChevronRightRegular />}
        disabled={page >= totalPages} onClick={() => onChange(page + 1)}
      />
    </div>
  );
}

function EditableCell({ value, onSave }) {
  const s = useDataTableStyles();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className={s.editBtn}
        title="Spustelėkite redaguoti"
      >
        {value ?? '—'}
      </button>
    );
  }
  const commit = () => { setEditing(false); if (String(draft) !== String(value ?? '')) onSave(draft); };
  return (
    <Input
      autoFocus size="small" value={draft}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); } }}
    />
  );
}

// Generic list table — thin wrapper around real Fluent Table/TableRow/TableCell
// with a columns-config API (renderCell/valueFormatter/inlineEditable) so
// existing admin pages port with a minimal diff (same shape as the old
// ui.jsx DataTable). `columns`: [{ field, headerName, renderHeader, renderCell,
// valueFormatter, inlineEditable, sortable, sortValue }].
//
// `sortable` (per-column, žr. 40 punktų sąrašo admin #2) — spustelėjus antraštę,
// rūšiuoja PERDUOTĄ `rows` masyvą lokaliai (klientinėje pusėje). Serverio
// puslapiuojamiems sąrašams (žr. AdminCRUDPage `paginated`) tai rūšiuos tik
// esamą, jau atkeltą puslapį — realaus visų eilučių rūšiavimo reikėtų serverio
// palaikymo, kurio čia sąmoningai neįtraukiame (apribojimas, ne klaida).
//
// `selectable`+`selectedIds`+`onSelectionChange` (žr. #3) — papildomas
// checkbox stulpelis masiniam veiksmų pasirinkimui; `selectedIds` yra Set<id>,
// valdomas iš išorės (controlled), kad kviečiantis puslapis galėtų rodyti
// "N pasirinkta" veiksmų juostą.
export function DataTable({
  columns, rows, getRowId, emptyMessage = 'Nėra įrašų', onRowClick, getRowClassName, onCellEdit,
  selectable = false, selectedIds, onSelectionChange,
}) {
  const s = useDataTableStyles();
  const [sort, setSort] = useState({ field: null, dir: 'asc' });

  const sortedRows = useMemo(() => {
    if (!sort.field) return rows;
    const col = columns.find((c) => c.field === sort.field);
    const getVal = col?.sortValue ? col.sortValue : (row) => row[sort.field];
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
      return sort.dir === 'asc'
        ? String(av).localeCompare(String(bv), 'lt')
        : String(bv).localeCompare(String(av), 'lt');
    });
    return copy;
  }, [rows, sort, columns]);

  const toggleSort = (field) => {
    setSort((prev) => (prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }));
  };

  const allSelected = selectable && rows.length > 0 && rows.every((r) => selectedIds?.has(getRowId(r)));
  const someSelected = selectable && !allSelected && rows.some((r) => selectedIds?.has(getRowId(r)));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? new Set() : new Set(rows.map(getRowId)));
  };
  const toggleOne = (id) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(next);
  };

  return (
    <div className={s.wrap}>
      <Table>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHeaderCell className={s.checkboxCell}>
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'mixed' : false}
                  onChange={toggleAll}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Pažymėti visus"
                />
              </TableHeaderCell>
            )}
            {columns.map((col) => (
              <TableHeaderCell
                key={col.field}
                className={mergeClasses(s.headerCell, col.sortable && s.headerCellSortable)}
                onClick={col.sortable ? () => toggleSort(col.field) : undefined}
              >
                {col.sortable ? (
                  <span className={s.headerCellInner}>
                    {col.renderHeader ? col.renderHeader() : col.headerName}
                    <span className={s.sortIcon}>
                      {sort.field === col.field
                        ? (sort.dir === 'asc' ? <ArrowSortUpRegular /> : <ArrowSortDownRegular />)
                        : <ArrowSortRegular />}
                    </span>
                  </span>
                ) : (
                  col.renderHeader ? col.renderHeader() : col.headerName
                )}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + (selectable ? 1 : 0)}>
                <EmptyState title={emptyMessage} />
              </TableCell>
            </TableRow>
          ) : sortedRows.map((row) => {
            const id = getRowId(row);
            return (
              <TableRow
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={mergeClasses(onRowClick && s.row, getRowClassName ? getRowClassName(row) : undefined)}
              >
                {selectable && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={!!selectedIds?.has(id)} onChange={() => toggleOne(id)} aria-label="Pažymėti eilutę" />
                  </TableCell>
                )}
                {columns.map((col) => {
                  const plainValue = !col.inlineEditable && !col.renderCell && !col.valueFormatter ? row[col.field] : undefined;
                  return (
                    <TableCell
                      key={col.field} className={s.cell}
                      title={plainValue != null && plainValue !== '' ? String(plainValue) : undefined}
                    >
                      {col.inlineEditable && onCellEdit
                        ? <EditableCell value={row[col.field]} onSave={(v) => onCellEdit(row, col.field, v)} />
                        : col.renderCell
                          ? col.renderCell({ value: row[col.field], row })
                          : (col.valueFormatter ? col.valueFormatter({ value: row[col.field] }) : (plainValue ?? '—'))}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// Shared admin toast system. Mount ONE <AdminToaster /> near the root of
// AdminLayout.jsx, then call `const notify = useAdminToast()` from any admin
// page and `notify('Išsaugota', 'success')` — replaces the old imperative
// ui.jsx <Toast open .../> prop-driven component with Fluent's real
// Toaster/useToastController API (Fluent toasts are inherently imperative,
// not a controlled `open` prop, so this hook is the idiomatic adapter).
export const ADMIN_TOASTER_ID = 'admin-toaster';

// top-end (ne bottom) — M365/Windows 11 sisteminio feedback'o konvencija
// (žr. 40 punktų sąrašo admin #9).
export function AdminToaster() {
  return <Toaster toasterId={ADMIN_TOASTER_ID} position="top-end" />;
}

const TOAST_INTENT = { success: 'success', error: 'error', warning: 'warning', info: 'info' };

export function useAdminToast() {
  const { dispatchToast } = useToastController(ADMIN_TOASTER_ID);
  return (message, severity = 'success') => {
    dispatchToast(
      <Toast><ToastTitle>{message}</ToastTitle></Toast>,
      { intent: TOAST_INTENT[severity] || 'success' },
    );
  };
}

export { useCardStyles, useHeadingStyles, useBadgeStyles, useLayoutStyles, useNavItemStyles, useId };
